(() => {
  'use strict';

  const runtimeKey = '__ldFloatingShortcutsRuntime';

  if (window[runtimeKey]) {
    window[runtimeKey].refresh();
    return;
  }

  class FloatingShortcutsController {
    constructor(root) {
      this.root = root;
      this.pageType = root.dataset.pageType || '';
      this.slots = Array.from(root.querySelectorAll('[data-slot]'));
      this.chooser = root.querySelector('[data-chooser]');
      this.chooserPanel = root.querySelector('.ld-floating-shortcuts__chooser-panel');
      this.main = document.querySelector('main');
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.lastFocusedElement = null;
      this.frameId = 0;
      this.lastActiveProductId = '';
      this.lastStoredScrollY = -1;
      this.mutationObserver = null;
      this.restoreTimers = [];
      this.restoreCancelled = false;
      this.usesExternalSceneProgress = false;

      this.storageKeys = {
        collectionUrl: 'ld_last_collection_url',
        collectionRestoreUrl: 'ld_last_collection_restore_url',
        collectionTitle: 'ld_last_collection_title',
        collectionScrollY: 'ld_last_collection_scroll_y',
        productViewportTop: 'ld_last_product_viewport_top',
        productUrl: 'ld_last_product_url',
        productTitle: 'ld_last_product_title',
        productId: 'ld_last_product_id',
        returnPending: 'ld_collection_return_pending',
      };
    }

    connect() {
      if (!this.root || this.root.dataset.ldShortcutsReady === 'true') return;
      this.root.dataset.ldShortcutsReady = 'true';
      this.syncChooserTriggers(this.chooserIsOpen());

      this.root.addEventListener('click', (event) => this.onRootClick(event), { signal: this.signal });
      document.addEventListener('click', (event) => this.onDocumentClick(event), {
        capture: true,
        signal: this.signal,
      });
      document.addEventListener('keydown', (event) => this.onKeydown(event), { signal: this.signal });
      document.addEventListener('ld:collection-chooser:open', (event) => this.openChooser(event.detail?.trigger), { signal: this.signal });
      document.addEventListener(
        'ldx:collection-scene-progress',
        (event) => {
          this.usesExternalSceneProgress = true;
          this.scheduleCollectionSync(String(event.detail?.productId || ''));
        },
        { signal: this.signal }
      );

      window.addEventListener('scroll', () => {
        /* Visibility remains viewport-owned even when a cinematic scene emits
           its own progress events. RAF coalescing prevents duplicate work. */
        this.scheduleCollectionSync();
      }, {
        passive: true,
        signal: this.signal,
      });
      window.addEventListener('resize', () => this.scheduleCollectionSync(), {
        passive: true,
        signal: this.signal,
      });
      window.addEventListener('pageshow', () => this.scheduleCollectionSync(), { signal: this.signal });

      if (this.pageType === 'collection' && this.main && 'MutationObserver' in window) {
        this.mutationObserver = new MutationObserver(() => this.scheduleCollectionSync());
        this.mutationObserver.observe(this.main, { childList: true, subtree: true });
      }

      this.rememberCollectionBase();
      this.syncProductReturnPath();
      this.restoreCollectionPosition();
      this.scheduleCollectionSync();
    }

    destroy() {
      this.closeChooser({ restoreFocus: false });
      this.abortController.abort();
      this.mutationObserver?.disconnect();
      this.mutationObserver = null;
      if (this.frameId) cancelAnimationFrame(this.frameId);
      this.frameId = 0;
      this.restoreTimers.forEach((timer) => window.clearTimeout(timer));
      this.restoreTimers = [];
      document.documentElement.classList.remove('ld-shortcuts-chooser-open');
      if (this.root) delete this.root.dataset.ldShortcutsReady;
    }

    storageGet(key) {
      try {
        return sessionStorage.getItem(key) || '';
      } catch (_error) {
        return '';
      }
    }

    storageSet(key, value) {
      try {
        if (value === '' || value === null || value === undefined) return;
        sessionStorage.setItem(key, String(value));
      } catch (_error) {
        // Storage can be unavailable in private browsing or embedded previews.
      }
    }

    storageRemove(key) {
      try {
        sessionStorage.removeItem(key);
      } catch (_error) {
        // No action required.
      }
    }

    toRelativeUrl(value) {
      if (!value) return '';

      try {
        const url = new URL(value, window.location.origin);
        if (url.origin !== window.location.origin) return '';
        return `${url.pathname}${url.search}${url.hash}`;
      } catch (_error) {
        return '';
      }
    }

    withoutHash(value) {
      const relative = this.toRelativeUrl(value);
      if (!relative) return '';
      const hashIndex = relative.indexOf('#');
      return hashIndex === -1 ? relative : relative.slice(0, hashIndex);
    }

    pathFromUrl(value) {
      const relative = this.toRelativeUrl(value);
      if (!relative) return '';

      try {
        return new URL(relative, window.location.origin).pathname;
      } catch (_error) {
        return '';
      }
    }

    isProductUrl(value) {
      return this.pathFromUrl(value).includes('/products/');
    }

    isCollectionUrl(value) {
      return this.pathFromUrl(value).includes('/collections/');
    }

    currentBaseUrl() {
      return `${window.location.pathname}${window.location.search}`;
    }

    sameOriginReferrer() {
      try {
        if (!document.referrer) return '';
        const url = new URL(document.referrer);
        if (url.origin !== window.location.origin) return '';
        return `${url.pathname}${url.search}${url.hash}`;
      } catch (_error) {
        return '';
      }
    }

    cleanTitle(value) {
      return String(value || '').replace(/\s+/g, ' ').trim();
    }

    setSlot(slotName, configuration) {
      const safeHref = this.toRelativeUrl(configuration.href);

      this.slots
        .filter((slot) => slot.dataset.slot === slotName)
        .forEach((slot) => {
          const eyebrow = slot.querySelector('[data-eyebrow]');
          const label = slot.querySelector('[data-label]');

          if (eyebrow && configuration.eyebrow) eyebrow.textContent = configuration.eyebrow;
          if (label && configuration.label) label.textContent = configuration.label;
          if (safeHref) slot.setAttribute('href', safeHref);

          if (configuration.action) slot.dataset.action = configuration.action;
          else slot.removeAttribute('data-action');

          if (configuration.ariaLabel) slot.setAttribute('aria-label', configuration.ariaLabel);
        });
    }

    isVisible(element) {
      if (!(element instanceof Element) || element.hidden || element.closest('[hidden]')) return false;
      const rect = element.getBoundingClientRect();
      return rect.width > 1 && rect.height > 1;
    }

    productInfoFromNode(node) {
      if (!(node instanceof Element)) return null;

      const link = node.matches('a[href*="/products/"]')
        ? node
        : node.querySelector(
            '[data-ld-story-product-link][href*="/products/"], .ldx-sakazuki-scene__title a[href*="/products/"], a[href*="/products/"]'
          );

      const url = this.toRelativeUrl(node.dataset.productUrl || link?.getAttribute('href') || link?.href);
      if (!this.isProductUrl(url)) return null;

      const titleNode = node.querySelector(
        '.ldx-sakazuki-scene__title, .ld-estate-story__chapter-title, [data-product-title], .product-card__title, h2, h3'
      );
      const title = this.cleanTitle(
        node.dataset.productTitle ||
          titleNode?.textContent ||
          link?.getAttribute('aria-label')?.replace(/^(Apri|Vedi)\s+/i, '') ||
          'Apri il prodotto'
      );

      return {
        node,
        url,
        title,
        productId: String(node.dataset.productId || ''),
        anchorId: node.id || '',
      };
    }

    productNodes() {
      const preferred = Array.from(
        document.querySelectorAll('main [data-ldx-product-scene], main [data-ld-story-chapter]')
      ).filter((node) => this.isVisible(node));

      if (preferred.length) return preferred;

      const uniqueNodes = [];
      const seen = new Set();

      document.querySelectorAll('main a[href*="/products/"]').forEach((link) => {
        const url = this.toRelativeUrl(link.getAttribute('href') || link.href);
        if (!this.isProductUrl(url) || seen.has(url)) return;

        const owner = link.closest('product-card, [data-product-card], .product-card, article, li') || link;
        if (!this.isVisible(owner)) return;

        seen.add(url);
        uniqueNodes.push(owner);
      });

      return uniqueNodes;
    }

    findProductById(productId) {
      if (!productId) return null;
      const node = this.productNodes().find(
        (candidate) => String(candidate.dataset.productId || '') === String(productId)
      );
      return node ? this.productInfoFromNode(node) : null;
    }

    activeProduct(preferredProductId = '') {
      if (preferredProductId) {
        const preferred = this.findProductById(preferredProductId);
        if (preferred) return preferred;
      }

      const activeNode = this.productNodes().find((node) => {
        if (!node.classList.contains('is-active')) return false;
        const rect = node.getBoundingClientRect();
        return rect.bottom > 0 && rect.top < window.innerHeight;
      });

      if (activeNode) {
        const activeInfo = this.productInfoFromNode(activeNode);
        if (activeInfo) return activeInfo;
      }

      const viewportCenter = Math.max(window.innerHeight, 1) / 2;
      let closest = null;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.productNodes().forEach((node) => {
        const info = this.productInfoFromNode(node);
        if (!info) return;

        const rect = node.getBoundingClientRect();
        const distance = Math.abs(rect.top + rect.height / 2 - viewportCenter);

        if (distance < closestDistance) {
          closest = info;
          closestDistance = distance;
        }
      });

      return closest;
    }

    productIsInViewport(info) {
      if (!info?.node || !this.isVisible(info.node)) return false;

      const rect = info.node.getBoundingClientRect();
      const headerBottom = Math.max(
        0,
        document.querySelector('#header-group, [data-header-group]')?.getBoundingClientRect().bottom || 0
      );
      const safetyInset = Math.min(80, Math.max(24, window.innerHeight * 0.08));
      const visibleTop = Math.max(headerBottom, 0);
      const visibleBottom = Math.max(visibleTop, window.innerHeight - safetyInset);
      const intersection = Math.min(rect.bottom, visibleBottom) - Math.max(rect.top, visibleTop);

      return intersection >= Math.min(96, rect.height * 0.12);
    }

    setCollectionShortcutVisibility(visible) {
      if (this.pageType !== 'collection') return;

      const canExpose = visible && !document.body.hasAttribute('data-r7-collection-opening');
      this.root.dataset.collectionProductVisible = String(canExpose);
      this.root.setAttribute('aria-hidden', String(!canExpose));
      this.root.toggleAttribute('inert', !canExpose);
    }

    collectionTitle() {
      return this.cleanTitle(
        this.root.dataset.currentCollectionTitle ||
          this.storageGet(this.storageKeys.collectionTitle) ||
          'Collezione'
      );
    }

    rememberCollectionBase() {
      if (this.pageType !== 'collection') return;
      this.storageSet(this.storageKeys.collectionUrl, this.currentBaseUrl());
      this.storageSet(this.storageKeys.collectionTitle, this.collectionTitle());
    }

    restoreUrlForProduct(info) {
      const anchor = info?.anchorId ? `#${encodeURIComponent(info.anchorId)}` : '';
      return `${this.currentBaseUrl()}${anchor}`;
    }

    rememberProductContext(info, force = false) {
      if (this.pageType !== 'collection' || !info) return;

      const scrollY = Math.max(0, Math.round(window.scrollY || window.pageYOffset || 0));
      const viewportTop = Math.round(info.node.getBoundingClientRect().top);
      const hasMeaningfulChange =
        force ||
        this.lastStoredScrollY < 0 ||
        info.productId !== this.lastActiveProductId ||
        Math.abs(scrollY - this.lastStoredScrollY) >= 96;

      if (!hasMeaningfulChange) return;

      this.lastActiveProductId = info.productId;
      this.lastStoredScrollY = scrollY;

      this.storageSet(this.storageKeys.collectionUrl, this.currentBaseUrl());
      this.storageSet(this.storageKeys.collectionRestoreUrl, this.restoreUrlForProduct(info));
      this.storageSet(this.storageKeys.collectionTitle, this.collectionTitle());
      this.storageSet(this.storageKeys.collectionScrollY, scrollY);
      this.storageSet(this.storageKeys.productViewportTop, viewportTop);
      this.storageSet(this.storageKeys.productUrl, info.url);
      this.storageSet(this.storageKeys.productTitle, info.title);
      this.storageSet(this.storageKeys.productId, info.productId);
    }

    syncCollectionShortcut(preferredProductId = '') {
      if (this.pageType !== 'collection') return;

      const info = this.activeProduct(preferredProductId);
      const productVisible = this.productIsInViewport(info);
      this.setCollectionShortcutVisibility(productVisible);
      if (!info || !productVisible) return;

      this.setSlot('next', {
        eyebrow: 'Prodotto in vista',
        label: 'Prodotto',
        href: info.url,
        ariaLabel: `Apri il prodotto in vista: ${info.title || 'prodotto selezionato'}`,
      });

      this.rememberProductContext(info);
    }

    scheduleCollectionSync(preferredProductId = '') {
      if (this.pageType !== 'collection') return;
      if (this.frameId) cancelAnimationFrame(this.frameId);

      this.frameId = requestAnimationFrame(() => {
        this.frameId = 0;
        this.syncCollectionShortcut(preferredProductId);
      });
    }

    updateBreadcrumbParent(title, href) {
      const breadcrumb = document.querySelector('[data-ld-path-breadcrumb]');
      if (!breadcrumb) return;

      const parentItem = breadcrumb.querySelector('[data-ld-path-parent-item]');
      const parentLink = breadcrumb.querySelector('[data-ld-path-parent-link]');
      const safeHref = this.toRelativeUrl(href);

      if (!parentItem || !parentLink || !safeHref) return;

      parentLink.textContent = title || 'Collezione';
      parentLink.setAttribute('href', safeHref);
      parentLink.setAttribute('aria-label', `Torna alla collezione ${title || ''}`.trim());
      parentItem.hidden = false;
    }

    syncProductReturnPath() {
      if (this.pageType !== 'product') return;

      const storedProductUrl = this.storageGet(this.storageKeys.productUrl);
      const currentProductUrl = this.toRelativeUrl(
        this.root.dataset.currentProductUrl || window.location.pathname
      );
      const storedProductMatches =
        this.isProductUrl(storedProductUrl) &&
        this.pathFromUrl(storedProductUrl) === this.pathFromUrl(currentProductUrl);

      const referrer = this.sameOriginReferrer();
      const cameFromCollection = this.isCollectionUrl(referrer);
      const storedCollectionBase = this.withoutHash(this.storageGet(this.storageKeys.collectionUrl));
      const storedRestoreUrl = this.toRelativeUrl(this.storageGet(this.storageKeys.collectionRestoreUrl));
      const storedRestoreBase = this.withoutHash(storedRestoreUrl);
      const referrerBase = this.withoutHash(referrer);

      let collectionUrl = '';
      let title = this.cleanTitle(this.storageGet(this.storageKeys.collectionTitle));

      if (cameFromCollection) {
        const storedContextMatchesReferrer =
          storedProductMatches && storedCollectionBase === referrerBase && storedRestoreBase === referrerBase;
        collectionUrl = storedContextMatchesReferrer ? storedRestoreUrl : referrer;
        if (!storedContextMatchesReferrer) title = this.root.dataset.defaultCatalogTitle || title;
      } else if (storedProductMatches) {
        collectionUrl = storedRestoreUrl || this.storageGet(this.storageKeys.collectionUrl);
      }

      if (this.isCollectionUrl(collectionUrl)) {
        const resolvedTitle = title || this.root.dataset.defaultCatalogTitle || 'Collezione precedente';
        this.setSlot('back', {
          eyebrow: 'Torna alla collezione',
          label: 'Collezione',
          href: collectionUrl,
          ariaLabel: `Torna alla collezione ${resolvedTitle}`,
        });
        this.updateBreadcrumbParent(resolvedTitle, collectionUrl);
      }

      this.storageSet(this.storageKeys.productUrl, currentProductUrl);
      this.storageSet(this.storageKeys.productTitle, this.root.dataset.currentProductTitle || document.title);
      this.storageSet(this.storageKeys.productId, this.root.dataset.currentProductId || '');
    }

    restoreCollectionPosition() {
      if (this.pageType !== 'collection' || this.storageGet(this.storageKeys.returnPending) !== '1') return;

      const storedBase = this.withoutHash(this.storageGet(this.storageKeys.collectionUrl));
      if (!storedBase || storedBase !== this.currentBaseUrl()) {
        this.storageRemove(this.storageKeys.returnPending);
        return;
      }

      this.storageRemove(this.storageKeys.returnPending);

      const storedY = Number.parseInt(this.storageGet(this.storageKeys.collectionScrollY), 10);
      const storedViewportTop = Number.parseInt(this.storageGet(this.storageKeys.productViewportTop), 10);
      const productId = this.storageGet(this.storageKeys.productId);
      const restoreUrl = this.toRelativeUrl(this.storageGet(this.storageKeys.collectionRestoreUrl));
      const hash = restoreUrl.includes('#') ? restoreUrl.slice(restoreUrl.indexOf('#') + 1) : '';
      let target = null;

      if (hash) {
        try {
          target = document.getElementById(decodeURIComponent(hash));
        } catch (_error) {
          target = null;
        }
      }

      const cancelRestore = () => {
        this.restoreCancelled = true;
        this.restoreTimers.forEach((timer) => window.clearTimeout(timer));
        this.restoreTimers = [];
      };

      window.addEventListener('wheel', cancelRestore, { once: true, passive: true, signal: this.signal });
      window.addEventListener('touchstart', cancelRestore, { once: true, passive: true, signal: this.signal });
      window.addEventListener('pointerdown', cancelRestore, { once: true, passive: true, signal: this.signal });

      const applyRestore = () => {
        if (this.restoreCancelled) return;

        if (!target && productId) target = this.findProductById(productId)?.node || null;

        let top = Number.isFinite(storedY) && storedY >= 0 ? storedY : null;
        if (target && Number.isFinite(storedViewportTop)) {
          const rect = target.getBoundingClientRect();
          top = Math.max(0, Math.round(window.scrollY + rect.top - storedViewportTop));
        }

        if (top !== null) {
          const maximum = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
          window.scrollTo({ top: Math.min(top, maximum), left: 0, behavior: 'auto' });
        } else if (target) {
          target.scrollIntoView({ block: 'start', behavior: 'auto' });
        }

        this.scheduleCollectionSync(productId);
      };

      requestAnimationFrame(() => requestAnimationFrame(applyRestore));
      [180, 520].forEach((delay) => {
        this.restoreTimers.push(window.setTimeout(applyRestore, delay));
      });
      window.addEventListener('load', applyRestore, { once: true, signal: this.signal });
    }

    flyweightRuntimeAvailable() {
      const scriptReady = Array.from(document.scripts).some((script) =>
        script.src.includes('assistant.flyweight.io/chat/index.js')
      );
      const nodeReady = Boolean(document.querySelector(
        'iframe[src*="assistant.flyweight.io"], [data-flyweight-chat], [id*="flyweight" i]'
      ));
      return scriptReady || nodeReady;
    }

    openFlyweightAssistant(trigger) {
      /* Flyweight remains the single owner of the chat. The shortcut only
         dispatches its official open event when the app runtime is ready.
         Otherwise the native href opens the dedicated assistant page. */
      if (!this.flyweightRuntimeAvailable()) return false;

      trigger.dataset.ldAiState = 'opening';
      trigger.setAttribute('aria-busy', 'true');
      window.dispatchEvent(new Event('flyweightchatopen'));

      window.setTimeout(() => {
        if (!trigger.isConnected) return;
        trigger.dataset.ldAiState = 'ready';
        trigger.removeAttribute('aria-busy');
      }, this.reducedMotion.matches ? 0 : 420);

      return true;
    }

    focusHeaderSearch() {
      const selector =
        '.dv-header-search__mobile-overlay:not([hidden]) .dv-header-search__mobile-input, .search-input--header, input[name="q"]';
      const visibleInput = document.querySelector(selector);
      const trigger = document.querySelector('.dv-header-search__trigger, [data-search-trigger]');

      if (!visibleInput && !trigger) return false;
      if (trigger) trigger.click();

      window.setTimeout(() => {
        const input = document.querySelector(selector);
        if (!input) return;

        input.scrollIntoView({
          behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
          block: 'center',
        });
        try {
          input.focus({ preventScroll: true });
        } catch (_error) {
          input.focus();
        }
      }, trigger ? 180 : 0);

      return true;
    }

    chooserIsOpen() {
      return Boolean(this.chooser && !this.chooser.hidden);
    }

    syncChooserTriggers(expanded) {
      if (!this.chooser?.id) return;
      this.root.querySelectorAll('[aria-controls]').forEach((control) => {
        if (control.getAttribute('aria-controls') === this.chooser.id) control.setAttribute('aria-expanded', String(expanded));
      });
    }

    chooserFocusableElements() {
      if (!this.chooser) return [];
      return Array.from(
        this.chooser.querySelectorAll(
          'a[href], button:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => this.isVisible(element));
    }

    openChooser(trigger) {
      if (!this.chooser || !this.chooserPanel) return false;
      if (this.chooserIsOpen()) return true;

      this.lastFocusedElement = trigger instanceof HTMLElement ? trigger : document.activeElement;
      this.chooser.hidden = false;
      this.chooser.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('ld-shortcuts-chooser-open');
      this.syncChooserTriggers(true);
      document.dispatchEvent(new CustomEvent('ld:home-suspend', { detail: { source: 'collection-chooser' } }));

      const firstFocusable = this.chooserFocusableElements()[0] || this.chooserPanel;
      try {
        firstFocusable.focus({ preventScroll: true });
      } catch (_error) {
        firstFocusable.focus();
      }

      requestAnimationFrame(() => {
        if (!this.signal.aborted && this.root.isConnected && this.chooserIsOpen() && !document.querySelector('dialog[open]') && !this.chooser.contains(document.activeElement)) this.chooserPanel.focus();
      });
      return true;
    }

    closeChooser({ restoreFocus = true, resumeHome = true } = {}) {
      if (!this.chooserIsOpen()) return;

      this.chooser.hidden = true;
      this.chooser.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('ld-shortcuts-chooser-open');
      this.syncChooserTriggers(false);
      if (resumeHome) document.dispatchEvent(new CustomEvent('ld:home-resume', { detail: { source: 'collection-chooser' } }));

      if (restoreFocus && this.lastFocusedElement instanceof HTMLElement && document.contains(this.lastFocusedElement)) {
        this.lastFocusedElement.focus();
      }
    }

    trapChooserFocus(event) {
      if (!this.chooserIsOpen() || event.key !== 'Tab') return;

      const focusable = this.chooserFocusableElements();
      if (!focusable.length) {
        event.preventDefault();
        this.chooserPanel?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onRootClick(event) {
      const chooserLink = event.target.closest('[data-chooser-link]');
      if (chooserLink instanceof HTMLAnchorElement && this.chooser?.contains(chooserLink)) {
        const href = this.toRelativeUrl(chooserLink.getAttribute('href') || chooserLink.href);
        if (href) {
          event.preventDefault();
          this.closeChooser({ restoreFocus: false, resumeHome: false });
          window.location.assign(href);
          return;
        }
      }

      const closeTrigger = event.target.closest('[data-chooser-close]');
      if (closeTrigger) {
        event.preventDefault();
        this.closeChooser();
        return;
      }

      const shortcut = event.target.closest('[data-slot]');
      if (!shortcut) return;

      if (this.pageType === 'collection' && shortcut.dataset.slot === 'next') {
        const info = this.activeProduct();
        if (info) this.rememberProductContext(info, true);
      }

      if (this.pageType === 'product' && shortcut.dataset.slot === 'back') {
        this.storageSet(this.storageKeys.returnPending, '1');
      }

      const action = shortcut.dataset.action;
      if (action === 'open-chooser' && this.openChooser(shortcut)) {
        event.preventDefault();
      } else if (action === 'focus-search' && this.focusHeaderSearch()) {
        event.preventDefault();
      } else if (action === 'open-flyweight-assistant' && this.openFlyweightAssistant(shortcut)) {
        event.preventDefault();
      } else if (action === 'open-welcome-assistant') {
        event.preventDefault();
        const welcomeTrigger = document.querySelector('[data-ldw-path="assistant"]');
        if (welcomeTrigger instanceof HTMLElement) {
          welcomeTrigger.click();
        } else {
          document.dispatchEvent(new CustomEvent('ldw:assistant:open', { detail: { trigger: shortcut } }));
        }
      }
    }

    onDocumentClick(event) {
      if (this.pageType === 'collection') {
        const productLink = event.target.closest('main a[href*="/products/"]');
        if (productLink) {
          const owner =
            productLink.closest(
              '[data-ldx-product-scene], [data-ld-story-chapter], product-card, [data-product-card], .product-card, article, li'
            ) || productLink;
          const info = this.productInfoFromNode(owner) || this.productInfoFromNode(productLink);
          if (info) this.rememberProductContext(info, true);
        }
      }

      if (this.pageType === 'product') {
        const breadcrumbParent = event.target.closest('[data-ld-path-parent-link]');
        if (breadcrumbParent) this.storageSet(this.storageKeys.returnPending, '1');
      }
    }

    onKeydown(event) {
      if (!this.chooserIsOpen()) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeChooser();
        return;
      }
      this.trapChooserFocus(event);
    }
  }

  const runtimeAbortController = new AbortController();
  let controller = null;

  const refresh = () => {
    const root = document.querySelector('[data-ld-floating-shortcuts]');

    if (controller && (!controller.root.isConnected || controller.root !== root)) {
      controller.destroy();
      controller = null;
    }

    if (!controller && root) {
      controller = new FloatingShortcutsController(root);
      controller.connect();
    } else {
      controller?.scheduleCollectionSync();
    }
  };

  const queueRefresh = () => requestAnimationFrame(refresh);

  document.addEventListener('shopify:section:load', queueRefresh, { signal: runtimeAbortController.signal });
  document.addEventListener('shopify:section:unload', queueRefresh, { signal: runtimeAbortController.signal });
  document.addEventListener('shopify:section:reorder', queueRefresh, { signal: runtimeAbortController.signal });

  window[runtimeKey] = { refresh };
  refresh();
})();
